/**
 * MGC · Store híbrido (Bloco 8).
 * - MODO ONLINE: consome /api/mgc/itens via Supabase JWT. Persiste snapshot local
 *   como cache de leitura para render otimista.
 * - MODO OFFLINE (sem JWT / API indisponível): usa localStorage puro com o mock
 *   inicial. Comportamento idêntico ao protótipo original.
 *
 * A API externa (`useMgcItems`, `moveMgcItem`, `useMgcAtivosCount`, `getMgcItems`)
 * é preservada — nenhum consumidor precisa mudar.
 */
import { useEffect, useState, useCallback } from "react";
import {
  apiListarItens, apiMoverItem, apiCriarItem, apiTimeline,
} from "@/lib/mgcApi";

const LS_KEY = "mgc_items_v1";
const LS_MODE = "mgc_mode";
const EVENT = "mgc:updated";
const AUTOR_MOCK = "Carlo";

// Mock offline (idem versão anterior)
const MOCK_INICIAL = [
  { id: "i1",  area: "ativos",   tipo: "piso",    sev: "critica", titulo: "Fluxo de caixa projetado abaixo do piso de sobrevivência em 3 semanas",           entrada: "07/Fev/2026", extra: null },
  { id: "i2",  area: "ativos",   tipo: "decisao", sev: "alta",    titulo: "Definir estratégia de precificação para o novo canal de vendas online",           entrada: "05/Fev/2026", extra: null },
  { id: "i3",  area: "ativos",   tipo: "alerta",  sev: "alta",    titulo: "CAC subiu 42% no último trimestre — investigar campanha do Instagram",             entrada: "06/Fev/2026", extra: null },
  { id: "i4",  area: "ativos",   tipo: "aviso",   sev: "media",   titulo: "Métrica 'Faturamento mensal' sem atualização há 12 dias",                          entrada: "04/Fev/2026", extra: null,
    fonteDado: { tipo: "standard", destino: "/app/v360/wizard?stage=E5&frente=F01", label: "Faturamento mensal · Wizard › KPIs › Finanças" } },
  { id: "i5",  area: "ativos",   tipo: "sinal",   sev: "baixa",   titulo: "Novo player entrou no mercado com preço 15% abaixo do seu (fonte: notícia setor)", entrada: "08/Fev/2026", extra: null },
  { id: "i11", area: "ativos",   tipo: "aviso",   sev: "baixa",   titulo: "KPI personalizado 'NPS trimestral' sem input há 45 dias",                          entrada: "03/Fev/2026", extra: null,
    fonteDado: { tipo: "customizada", destino: "/app/personalizacao/kpis", label: "NPS trimestral · Personalização › KPIs" } },
  { id: "i6",  area: "adiados",  tipo: "decisao", sev: "media",   titulo: "Renegociar contrato com fornecedor de embalagens",                                 entrada: "28/Jan/2026", extra: { adiadoAte: "15/Fev/2026", diasRestantes: 6, motivo: "Aguardando proposta atualizada" } },
  { id: "i7",  area: "adiados",  tipo: "alerta",  sev: "media",   titulo: "Retenção de clientes caiu para 74% (meta: 85%)",                                    entrada: "30/Jan/2026", extra: { adiadoAte: "11/Fev/2026", diasRestantes: 2, motivo: "Sem dados suficientes para agir agora" } },
  { id: "i8",  area: "ignorados",tipo: "sinal",   sev: "baixa",   titulo: "Concorrente lançou programa de fidelidade",                                         entrada: "20/Jan/2026", extra: { ignoradoEm: "22/Jan/2026", motivo: "Fora do foco estratégico atual" } },
  { id: "i9",  area: "cientes",  tipo: "piso",    sev: "alta",    titulo: "Sustentabilidade do dono: você não tira folga há 47 dias",                          entrada: "01/Fev/2026", extra: { cienteEm: "02/Fev/2026" } },
  { id: "i10", area: "cientes",  tipo: "piso",    sev: "critica", titulo: "Conformidade: alvará de funcionamento vence em 18 dias",                            entrada: "22/Jan/2026", extra: { cienteEm: "23/Jan/2026" } },
];

// ─────────────────────────────────────────────────────────────────────────────
// Storage helpers (cache local — modo offline OU cache do modo online)
// ─────────────────────────────────────────────────────────────────────────────
function safeRead() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return garantirEventoCriado(MOCK_INICIAL);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? garantirEventoCriado(parsed) : garantirEventoCriado(MOCK_INICIAL);
  } catch {
    return garantirEventoCriado(MOCK_INICIAL);
  }
}
function safeWrite(items) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(EVENT));
  } catch (e) { console.warn("mgcStore write failed", e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversores API ↔ formato interno
// ─────────────────────────────────────────────────────────────────────────────
function apiToLocal(row) {
  return {
    id: row.id,
    empresa_id: row.empresa_id,
    tipo: row.tipo,
    sev: row.sev,
    area: row.area,
    titulo: row.titulo,
    entrada: row.entrada ? formatarEntradaBR(row.entrada) : null,
    extra: row.extra || null,
    fonteDado: row.fonte_dado || null,
    readiadoCount: row.readiado_count || 0,
    origem: row.origem,
    eventos: [], // timeline é lazy-loaded via apiTimeline
  };
}
function formatarEntradaBR(iso) {
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
    return `${dd}/${meses[d.getMonth()]}/${d.getFullYear()}`;
  } catch { return iso; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Modo (persistido para evitar flicker entre sessões)
// ─────────────────────────────────────────────────────────────────────────────
function getMode() { return localStorage.getItem(LS_MODE) || "unknown"; }
function setMode(m) {
  if (localStorage.getItem(LS_MODE) !== m) {
    localStorage.setItem(LS_MODE, m);
    window.dispatchEvent(new Event(EVENT));
  }
}
export function getMgcMode() { return getMode(); }

// ─────────────────────────────────────────────────────────────────────────────
// Refresh online: baixa itens do backend e atualiza cache local
// ─────────────────────────────────────────────────────────────────────────────
async function refetchFromApi() {
  const resp = await apiListarItens();
  if (resp.ok) {
    const items = (resp.data.itens || []).map(apiToLocal);
    safeWrite(items);
    setMode("online");
    return items;
  }
  if (resp.offline) {
    setMode("offline");
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// API pública (compatível com versão anterior)
// ─────────────────────────────────────────────────────────────────────────────
export function getMgcItems() { return safeRead(); }
export function setMgcItems(items) { safeWrite(items); }

/** Phase 12.37 · Força refetch imediato do backend (usado pelo botão
 * "Rodar motor MGC" para atualizar a UI logo após o POST /motor/executar). */
export async function refreshMgcItems() { return await refetchFromApi(); }

/** Move um item — chama backend se ONLINE; senão offline puro. */
export async function moveMgcItem(id, targetArea, extraOverride = null) {
  const mode = getMode();

  // Tenta pela API primeiro se modo online (ou desconhecido — vale a pena tentar)
  if (mode !== "offline") {
    const resp = await apiMoverItem(id, targetArea, extraOverride, extraOverride?.motivo || null);
    if (resp.ok) {
      setMode("online");
      await refetchFromApi();
      return;
    }
    if (!resp.offline) {
      // 4xx real (não encontrado, permissão, etc) — mostra erro no console mas segue com offline
      console.warn("[mgc] mover falhou:", resp.error);
    }
    setMode("offline");
  }

  // Fallback offline: mesma lógica anterior
  const items = safeRead();
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mesesAbrev = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const hojeStr = `${dd}/${mesesAbrev[now.getMonth()]}/${now.getFullYear()}`;
  const nowIso = now.toISOString();
  const next = items.map((it) => {
    if (it.id !== id) return it;
    let extra = extraOverride;
    if (!extra) {
      if (targetArea === "ativos")    extra = null;
      if (targetArea === "ignorados") extra = { ignoradoEm: hojeStr };
      if (targetArea === "cientes")   extra = { cienteEm: hojeStr, cienteEmIso: nowIso };
    } else if (targetArea === "cientes" && !extra.cienteEmIso) {
      extra = { ...extra, cienteEmIso: nowIso };
    }
    let readiadoCount = it.readiadoCount || 0;
    if (it.area === "adiados" && targetArea === "ativos") readiadoCount += 1;
    const eventos = Array.isArray(it.eventos) ? [...it.eventos] : [];
    let tipoEvento = "moveu";
    if (it.area !== targetArea) {
      if (targetArea === "adiados")   tipoEvento = "adiado";
      else if (targetArea === "ignorados") tipoEvento = "ignorado";
      else if (targetArea === "cientes")   tipoEvento = "ciente";
      else if (targetArea === "ativos" && it.area === "adiados")   tipoEvento = "retomado";
      else if (targetArea === "ativos" && it.area === "ignorados") tipoEvento = "reativado";
      else if (targetArea === "ativos" && it.area === "cientes")   tipoEvento = "desfez_ciente";
    }
    eventos.push({ tipo: tipoEvento, timestamp: nowIso, autor: AUTOR_MOCK, de: it.area, para: targetArea, dados: extra || null });
    return { ...it, area: targetArea, extra, eventos, readiadoCount };
  });
  safeWrite(next);
}

function garantirEventoCriado(items) {
  return items.map((it) => {
    if (Array.isArray(it.eventos) && it.eventos.length > 0) return it;
    return { ...it, eventos: [{ tipo: "criado", timestamp: null, timestampBR: it.entrada, autor: "Motor MGC", dados: { tipo: it.tipo, sev: it.sev } }] };
  });
}

/** Cria item — se ONLINE, no backend; senão apenas local. */
export async function createMgcItem(payload) {
  const mode = getMode();
  if (mode !== "offline") {
    const resp = await apiCriarItem(payload);
    if (resp.ok) { setMode("online"); await refetchFromApi(); return resp.data; }
    setMode("offline");
  }
  // Fallback: gera id local e persiste
  const id = `local-${Date.now()}`;
  const item = { id, area: "ativos", eventos: [], readiadoCount: 0, ...payload, entrada: (payload.entrada || new Date().toISOString()) };
  safeWrite([item, ...safeRead()]);
  return item;
}

/** Timeline — busca do backend se ONLINE, senão do próprio item.eventos */
export async function fetchTimeline(id) {
  if (getMode() === "online") {
    const resp = await apiTimeline(id);
    if (resp.ok) return resp.data.eventos;
  }
  const it = safeRead().find(i => i.id === id);
  // Adapta eventos locais para o formato do backend (para o drawer renderizar igual)
  return (it?.eventos || []).slice().reverse().map(e => ({
    tipo: e.tipo,
    criado_em: e.timestamp,
    criado_em_br: e.timestampBR,
    autor_nome: e.autor,
    de_area: e.de,
    para_area: e.para,
    dados: e.dados,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────
export function useMgcItems() {
  const [items, setItems] = useState(() => safeRead());
  // Ao montar, tenta sincronizar com backend (se JWT existir)
  useEffect(() => {
    refetchFromApi(); // silencioso — safeWrite dispara EVENT
  }, []);
  useEffect(() => {
    const onUpdate = () => setItems(safeRead());
    window.addEventListener(EVENT, onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener(EVENT, onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, []);
  const persist = useCallback((updater) => {
    const current = safeRead();
    const next = typeof updater === "function" ? updater(current) : updater;
    safeWrite(next);
    setItems(next);
  }, []);
  return [items, persist];
}

export function useMgcAtivosCount() {
  const [count, setCount] = useState(() => safeRead().filter(i => i.area === "ativos").length);
  useEffect(() => {
    const onUpdate = () => setCount(safeRead().filter(i => i.area === "ativos").length);
    window.addEventListener(EVENT, onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener(EVENT, onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, []);
  return count;
}
